import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { InspectionProgramItemDto } from '../types/dtos/inspection';
import { createInspectionProgramItemService } from '../services/inspection-program-item.service';
import { PaginationParams } from '../types/common';

const INSPECTION_PROGRAM_ITEMS_KEY = 'inspection-program-items';

export const useInspectionProgramItems = (
  organizationId: string,
  programId: string,
  inspectionProgramId: string,
  params?: PaginationParams
) => {
  const inspectionProgramItemService = createInspectionProgramItemService(
    organizationId,
    programId,
    inspectionProgramId
  );

  return useQuery({
    queryKey: [INSPECTION_PROGRAM_ITEMS_KEY, organizationId, programId, inspectionProgramId, params],
    queryFn: () => inspectionProgramItemService.findAll(params).then((res) => res.data),
    enabled: !!organizationId && !!programId && !!inspectionProgramId
  });
};

export const useInspectionProgramItem = (
  organizationId: string,
  programId: string,
  inspectionProgramId: string,
  id: string
) => {
  const inspectionProgramItemService = createInspectionProgramItemService(
    organizationId,
    programId,
    inspectionProgramId
  );

  return useQuery({
    queryKey: [INSPECTION_PROGRAM_ITEMS_KEY, organizationId, programId, inspectionProgramId, id],
    queryFn: () => inspectionProgramItemService.findById(id).then((res) => res.data),
    enabled: !!organizationId && !!programId && !!inspectionProgramId && !!id
  });
};

export const useInspectionProgramItemsByType = (
  organizationId: string,
  programId: string,
  inspectionProgramId: string,
  type: string
) => {
  const inspectionProgramItemService = createInspectionProgramItemService(
    organizationId,
    programId,
    inspectionProgramId
  );

  return useQuery({
    queryKey: [INSPECTION_PROGRAM_ITEMS_KEY, organizationId, programId, inspectionProgramId, 'type', type],
    queryFn: () => inspectionProgramItemService.findByType(type).then((res) => res.data),
    enabled: !!organizationId && !!programId && !!inspectionProgramId && !!type
  });
};

export const useCreateInspectionProgramItem = (
  organizationId: string,
  programId: string,
  inspectionProgramId: string
) => {
  const queryClient = useQueryClient();
  const inspectionProgramItemService = createInspectionProgramItemService(
    organizationId,
    programId,
    inspectionProgramId
  );

  return useMutation({
    mutationFn: (item: Omit<InspectionProgramItemDto, 'id'>) => 
      inspectionProgramItemService.create(item).then((res) => res.data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [INSPECTION_PROGRAM_ITEMS_KEY, organizationId, programId, inspectionProgramId]
      });
    }
  });
};

export const useUpdateInspectionProgramItem = (
  organizationId: string,
  programId: string,
  inspectionProgramId: string
) => {
  const queryClient = useQueryClient();
  const inspectionProgramItemService = createInspectionProgramItemService(
    organizationId,
    programId,
    inspectionProgramId
  );

  return useMutation({
    mutationFn: ({ id, item }: { id: string; item: Partial<Omit<InspectionProgramItemDto, 'id'>> }) =>
      inspectionProgramItemService.update(id, item).then((res) => res.data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({
        queryKey: [INSPECTION_PROGRAM_ITEMS_KEY, organizationId, programId, inspectionProgramId]
      });
      queryClient.invalidateQueries({
        queryKey: [INSPECTION_PROGRAM_ITEMS_KEY, organizationId, programId, inspectionProgramId, id]
      });
    }
  });
};

export const useUpdateInspectionProgramItemStatus = (
  organizationId: string,
  programId: string,
  inspectionProgramId: string
) => {
  const queryClient = useQueryClient();
  const inspectionProgramItemService = createInspectionProgramItemService(
    organizationId,
    programId,
    inspectionProgramId
  );

  return useMutation({
    mutationFn: ({ id, status }: { id: string; status: string }) =>
      inspectionProgramItemService.updateStatus(id, status).then((res) => res.data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({
        queryKey: [INSPECTION_PROGRAM_ITEMS_KEY, organizationId, programId, inspectionProgramId]
      });
      queryClient.invalidateQueries({
        queryKey: [INSPECTION_PROGRAM_ITEMS_KEY, organizationId, programId, inspectionProgramId, id]
      });
    }
  });
};

export const useUpdateInspectionProgramItemValue = (
  organizationId: string,
  programId: string,
  inspectionProgramId: string
) => {
  const queryClient = useQueryClient();
  const inspectionProgramItemService = createInspectionProgramItemService(
    organizationId,
    programId,
    inspectionProgramId
  );

  return useMutation({
    mutationFn: ({ id, value }: { id: string; value: string }) =>
      inspectionProgramItemService.updateValue(id, value).then((res) => res.data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({
        queryKey: [INSPECTION_PROGRAM_ITEMS_KEY, organizationId, programId, inspectionProgramId]
      });
      queryClient.invalidateQueries({
        queryKey: [INSPECTION_PROGRAM_ITEMS_KEY, organizationId, programId, inspectionProgramId, id]
      });
    }
  });
};

export const useAddInspectionProgramItemComment = (
  organizationId: string,
  programId: string,
  inspectionProgramId: string
) => {
  const queryClient = useQueryClient();
  const inspectionProgramItemService = createInspectionProgramItemService(
    organizationId,
    programId,
    inspectionProgramId
  );

  return useMutation({
    mutationFn: ({ id, comment }: { id: string; comment: string }) =>
      inspectionProgramItemService.addComment(id, comment).then((res) => res.data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({
        queryKey: [INSPECTION_PROGRAM_ITEMS_KEY, organizationId, programId, inspectionProgramId]
      });
      queryClient.invalidateQueries({
        queryKey: [INSPECTION_PROGRAM_ITEMS_KEY, organizationId, programId, inspectionProgramId, id]
      });
    }
  });
};

export const useAddInspectionProgramItemEvidence = (
  organizationId: string,
  programId: string,
  inspectionProgramId: string
) => {
  const queryClient = useQueryClient();
  const inspectionProgramItemService = createInspectionProgramItemService(
    organizationId,
    programId,
    inspectionProgramId
  );

  return useMutation({
    mutationFn: ({ id, evidenceUrl }: { id: string; evidenceUrl: string }) =>
      inspectionProgramItemService.addEvidence(id, evidenceUrl).then((res) => res.data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({
        queryKey: [INSPECTION_PROGRAM_ITEMS_KEY, organizationId, programId, inspectionProgramId]
      });
      queryClient.invalidateQueries({
        queryKey: [INSPECTION_PROGRAM_ITEMS_KEY, organizationId, programId, inspectionProgramId, id]
      });
    }
  });
};

export const useDeleteInspectionProgramItem = (
  organizationId: string,
  programId: string,
  inspectionProgramId: string
) => {
  const queryClient = useQueryClient();
  const inspectionProgramItemService = createInspectionProgramItemService(
    organizationId,
    programId,
    inspectionProgramId
  );

  return useMutation({
    mutationFn: (id: string) => inspectionProgramItemService.delete(id).then((res) => res.data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [INSPECTION_PROGRAM_ITEMS_KEY, organizationId, programId, inspectionProgramId]
      });
    }
  });
};

export const useUploadInspectionProgramItemFile = (
  organizationId: string,
  programId: string,
  inspectionProgramId: string,
  itemId: string
) => {
  const inspectionProgramItemService = createInspectionProgramItemService(
    organizationId,
    programId,
    inspectionProgramId
  );

  return useMutation({
    mutationFn: async ({ file, description, onUploadProgress }: { file: File; description?: string; onUploadProgress?: (progressEvent: ProgressEvent) => void }) => {
      return inspectionProgramItemService.uploadFile(itemId, file, description, onUploadProgress);
    },
  });
}; 